import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  Gem, 
  CheckCircle,
  AlertCircle,
  Calendar,
  TrendingUp,
  SquareX
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { multiNivelService, MultiNivelExtrato } from '@/services/multi-nivel.service';

interface ExtratosMultiNivelProps {
  usuarioId?: number;
}

const ExtratosMultiNivel: React.FC<ExtratosMultiNivelProps> = ({ usuarioId }) => {
  const navigate = useNavigate();
  const [extratos, setExtratos] = useState<MultiNivelExtrato[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadExtratos();
  }, [usuarioId]);

  const loadExtratos = async () => {
    try {
      setLoading(true);
      
      // Get user ID from localStorage if not provided
      let userId = usuarioId;
      if (!userId) {
        const userDataString = localStorage.getItem('userlogged');
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          userId = userData.id;
        }
      }

      if (!userId) {
        console.error('User ID not found');
        return;
      }

      const data = await multiNivelService.getExtratosByUserId(userId);
      setExtratos(data);
    } catch (error) {
      console.error('Erro ao carregar extratos:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatMonth = (mesReferencia: string) => {
    const [year, month] = mesReferencia.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    const dateLocale = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    return dateLocale.charAt(0).toUpperCase() + dateLocale.slice(1);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Don't render anything if no extratos or still loading
  if (loading || extratos.length === 0) {
    return null;
  }

  return (
    <div className="px-4 mb-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Repasses
          </h2>
          {/* <button 
            onClick={() => navigate('/extrato-mensal')}
            className="text-sm text-primary hover:text-primary/80 font-medium"
          >
            Ver todos
          </button> */}
        </div>

        <div className="space-y-3">
          {extratos.slice(0, 3).map((extrato) => (
            <Card 
              key={extrato.id} 
              className={`${
                extrato.minimo_atingido 
                  ? 'bg-green-50 border-green-200 hover:bg-green-100' 
                  : 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100'
              } hover:shadow-md transition-all cursor-pointer`}
              onClick={() => navigate('/extrato-mensal')}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-center gap-2 mb-1 w-full">
                    <p className="font-medium text-gray-900 text-sm">
                        {formatMonth(extrato.mes_referencia)}
                    </p>
                    {!extrato.minimo_atingido && (
                        <Badge 
                            className={`text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800`}
                        >
                            Mínimo Não Atingido
                        </Badge>
                    )}
                </div>
                <div className="flex items-center justify-between">
                  {/* Info principal */}
                  <div className="flex items-center gap-3">
                    <div className={`p-2  rounded-lg shadow-sm ${
                      extrato.minimo_atingido ? 'bg-green-200' : 'bg-yellow-200'
                    }`}>
                      {extrato.minimo_atingido ? (
                        <CheckCircle className="w-5 h-5 text-green-700" />
                      ) : (
                        <SquareX className="w-5 h-5 text-yellow-700" />
                      )}
                    </div>
                   
                    <div className="flex-1">
                      <div className="space-y-1">
                        <div className="flex gap-4 text-xs text-gray-600">
                          <span>Nível 1: <b>{extrato.total_diamantes_nivel_1}</b></span>
                          <span>Nível 2: <b>{extrato.total_diamantes_nivel_2}</b></span>
                        </div>
                        <div className="flex gap-4 text-xs text-gray-600">
                          <span>Nível 3: <b>{extrato.total_diamantes_nivel_3}</b></span>
                          <span>Bônus: <b>{extrato.total_diamantes_aprovacao}</b></span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Total de diamantes */}
                  <div className="text-right">
                    <p className={`text-lg font-bold ${extrato.minimo_atingido ? 'text-purple-700' : 'text-gray-700 line-through'} flex items-center justify-end`}>
                      {extrato.total_diamantes_geral} <Gem className="w-3 h-3 ml-2" />
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExtratosMultiNivel;